import { Grid } from '@mui/material';
import { dateFormatLongDate, parseSecondsToHMText } from '@nx-smartmonkey/shared/helpers';
import { RoutalLoader, RoutalPalette } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import More3Points from '../../../../assets/svg/More3Points';
import TotalDistanceIcon from '../../../../assets/svg/Plan/DetailHeader/TotalDistanceIcon';
import TotalTimeIcon from '../../../../assets/svg/Plan/DetailHeader/TotalTimeIcon';
import { getPlanStatusColor } from '../../../../constants/Colors';
import * as routing from '../../../../constants/Routing';
import { PlanStatusDialog } from '../../../../dialogs/PlanStatusDialog';
import { Driver } from '../../../../domain/driver/Driver';
import { PlanFlat } from '../../../../domain/plan/PlanFlat';
import { Project } from '../../../../domain/project/Project';
import { kmToSM } from '../../../../helpers/Units';
import InputText from '../../../../react-components/InputText';
import Text from '../../../../react-components/Text';
import { PlanStatusIcon } from '../../shared/PlanStatusIcon';
import { AnalyticsDialog } from '../dialogs/analytics/AnalyticsDialog';
import { DispatchDialog } from '../dialogs/DispatchDialog';
import { GeoFencesDialogOverview } from '../dialogs/geo-fences/GeoFencesDialogOverview';
import { OptimizerOptionsDialog } from '../dialogs/OptimizerOptionsDialog';
import { useOptimizePlan } from '../hooks/plan-details/useOptimizePlan';
import { useRetrieveActivePlanDrivers } from '../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useUpdateActivePlan } from '../hooks/plan-details/useUpdateActivePlan';
import { PlanActionsMenu } from '../menus/PlanActionsMenu';
import { OptimizationDialog } from './OptimizationDialog';
import { PlanTabs } from './PlanTabs';

type PlanHeaderProps = {
  activePlan?: PlanFlat;
  isLoading: boolean;
  activeProject?: Project;
};

export const PlanHeader = ({ activePlan, isLoading, activeProject }: PlanHeaderProps) => {
  const navigate = useNavigate();
  const { data: drivers } = useRetrieveActivePlanDrivers();
  const { run: updateActivePlan } = useUpdateActivePlan();
  const { run: optimizePlan } = useOptimizePlan();

  // const { data: flatPlans, run: fetchFlatPlans } = useRetrieveFlatPlans({ project: activeProject });
  const [dialog, setDialog] = useState<
    | `dispatch`
    | `optimize`
    | `warnings`
    | `optimizerOptions`
    | `update_status`
    | `geofence_dialog`
    | `analytics`
    | undefined
  >(undefined);

  // !!!!!! This is commented because it will be needed later
  // const [nextPlan, setNextPlan] = useState<string>(``);
  // const [previousPlan, setPreviousPlan] = useState<string>(``);

  // useEffect(() => {
  //   fetchFlatPlans();
  // }, [fetchFlatPlans]);

  // const parsedFlatPlans = flatPlans?.map((plan) => ({
  //   id: plan.id,
  //   execution_date: new Date(plan.execution_date),
  // }));
  // const sortedFlatPlans = parsedFlatPlans?.sort((a, b) => a.execution_date.getTime() - b.execution_date.getTime());

  // useEffect(() => {
  //   if (sortedFlatPlans && sortedFlatPlans.length > 0 && activePlan) {
  //     const activePlanIndex = sortedFlatPlans.findIndex((plan) => plan.id === activePlan.id);
  //     if (activePlanIndex + 1 < sortedFlatPlans.length) {
  //       setNextPlan(sortedFlatPlans[activePlanIndex + 1]?.id || ``);
  //     } else {
  //       setNextPlan(``);
  //     }
  //     if (activePlanIndex - 1 >= 0) {
  //       setPreviousPlan(sortedFlatPlans[activePlanIndex - 1]?.id || ``);
  //     } else {
  //       setPreviousPlan(``);
  //     }
  //   }
  // }, [activePlan, activePlan?.id, sortedFlatPlans]);
  // !!!!!!

  /**
   * Total duration is the amount of the difference between planned_end_time and planned_start_time of each driver.
   */
  const getRoutesTotalDuration = (): number => {
    let totalDuration = 0;
    (drivers ?? []).forEach((driver: Driver) => {
      totalDuration += (driver.planned_end_time ?? 0) - (driver.planned_start_time ?? 0);
    });
    return totalDuration;
  };

  /**
   * Total distance is the amount of distance_to_next_stop of each
   * driver stop and the first driver stop distance_to_previous_stop.
   */
  const getPlanTotalDistance = (): string => {
    let totalDistance = 0;
    (drivers ?? []).forEach((driver: Driver) => {
      if (driver.stops.length > 0) {
        const distanceToFirstService = driver.stops[0].distance_to_previous_stop ?? 0;
        totalDistance +=
          (driver.stops.reduce((ps: any, ns: any) => ps + (ns.distance_to_next_stop || 0), distanceToFirstService) ||
            0) / 1000;
      }
    });
    return `${
      kmToSM(totalDistance, activeProject?.units?.distance) > 100
        ? Math.floor(kmToSM(totalDistance, activeProject?.units?.distance))
        : kmToSM(totalDistance, activeProject?.units?.distance).toFixed(1)
    }${activeProject?.units?.distance}`;
  };

  return (
    <>
      <Grid
        wrap="nowrap"
        container
        direction="row"
        style={{
          height: `38px`,
          borderBottom: `1px solid ${RoutalPalette.neutral10}`,
          backgroundColor: RoutalPalette.neutral02n,
        }}
      >
        {isLoading || !activePlan ? (
          <Grid
            item
            container
            direction="column"
            style={{
              position: `relative`,
              width: `380px`,
              padding: `0 8px`,
              borderRight: `1px solid ${RoutalPalette.neutral10}`,
            }}
          >
            <RoutalLoader width={22} />
          </Grid>
        ) : (
          <Grid
            item
            container
            direction="row"
            style={{
              position: `relative`,
              width: `380px`,
              padding: `0 8px`,
              borderRight: `1px solid ${RoutalPalette.neutral10}`,
            }}
            wrap="nowrap"
            alignItems="center"
          >
            {/* <DropdownIcon
              style={{
                color: `${previousPlan ? RoutalPalette.primary80 : RoutalPalette.neutral10}`,
                transform: `rotate(+90deg)`,
                height: `20px`,
                width: `20px`,
                padding: `4px`,
                cursor: previousPlan ? `pointer` : undefined,
              }}
              intlId="back"
              onClick={() => {
                if (previousPlan && !isLoading)
                  navigate(
                    routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, activeProject!.id).replace(
                      `:planId`,
                      previousPlan
                    )
                  );
              }}
            /> */}
            <Grid
              item
              container
              direction="row"
              alignItems="center"
              wrap="nowrap"
              justifyContent="space-evenly"
              style={
                {
                  // borderLeft: `1px solid ${RoutalPalette.neutral10}`,
                  // borderRight: `1px solid ${RoutalPalette.neutral10}`,
                }
              }
            >
              <Text style={{ fontSize: `14px`, fontWeight: 500 }}>
                {dateFormatLongDate(activePlan?.execution_date)}
              </Text>
              <InputText
                fieldValue={activePlan?.label ?? ``}
                handleEnter={(value) => {
                  updateActivePlan({ label: value });
                }}
                forceFocusedBorderColor
                focusedBorderColor={RoutalPalette.secondary60}
                inputStyles={{ fontSize: `14px`, color: RoutalPalette.secondary00 }}
                inputContainerStyles={{
                  padding: 0,
                  alignContent: `center`,
                  width: `280px`,
                  height: `26px`,
                  margin: 4,
                }}
                variant={`clean`}
                bGColor={`transparent`}
                hoverBGColor={RoutalPalette.neutral05}
                inputProps={{
                  style: {
                    minHeight: `18px`,
                    fontSize: `14px`,
                    height: `18px`,
                    overflow: `hidden`,
                    color: RoutalPalette.secondary00,
                  },
                }}
              />
            </Grid>
            {/* <DropdownIcon
              style={{
                color: `${nextPlan ? RoutalPalette.primary80 : RoutalPalette.neutral10}`,
                transform: `rotate(-90deg)`,
                height: `20px`,
                width: `20px`,
                padding: `4px`,
                cursor: nextPlan ? `pointer` : undefined,
              }}
              intlId="next"
              onClick={() => {
                if (nextPlan && !isLoading)
                  navigate(
                    routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, activeProject!.id).replace(
                      `:planId`,
                      nextPlan
                    )
                  );
              }}
            /> */}
          </Grid>
        )}

        <Grid item container wrap="nowrap" style={{ flex: 1 }}>
          <Grid container style={{ width: `100%` }}>
            <PlanTabs activeProject={activeProject} activePlan={activePlan} />
          </Grid>

          <Grid
            container
            justifyContent="space-between"
            style={{ width: `fit-content`, display: `row`, flexWrap: `nowrap` }}
          >
            <Grid
              item
              container
              direction="row"
              wrap="nowrap"
              style={{
                padding: `0px 24px`,
                gap: `24px`,
                borderLeft: `1px solid ${RoutalPalette.neutral10}`,
                justifyContent: `center`,
                width: `fit-content`,
                cursor: `pointer`,
              }}
              onClick={() => {
                setDialog(`analytics`);
              }}
            >
              {isLoading || !activePlan ? (
                <Grid style={{ width: `143px` }}>
                  <RoutalLoader width={22} />
                </Grid>
              ) : (
                <>
                  <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                    <TotalTimeIcon />
                    <Text
                      style={{
                        marginLeft: `4px`,
                        fontSize: `12px`,
                        cursor: `pointer`,
                        whiteSpace: `nowrap`,
                      }}
                    >
                      {parseSecondsToHMText(getRoutesTotalDuration())}
                    </Text>
                  </Grid>

                  <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                    <TotalDistanceIcon />
                    <Text
                      style={{
                        marginLeft: `4px`,
                        fontSize: `12px`,
                        cursor: `pointer`,
                      }}
                    >
                      {getPlanTotalDistance()}
                    </Text>
                  </Grid>
                </>
              )}
            </Grid>

            <Grid
              container
              direction="row"
              wrap="nowrap"
              alignItems="center"
              justifyContent="center"
              style={{ width: `fit-content`, padding: `0px 13px`, borderLeft: `1px solid ${RoutalPalette.neutral10}` }}
            >
              {activePlan?.status && (
                <Grid
                  style={{
                    padding: `6px 10px`,
                    display: `flex`,
                    justifyContent: `center`,
                    alignItems: `center`,
                    borderRadius: `5px`,
                    backgroundColor: getPlanStatusColor(activePlan.status, `light`),
                    border: `1px solid ${getPlanStatusColor(activePlan.status, `medium`)}`,
                    cursor: `pointer`,
                  }}
                  onClick={() => {
                    setDialog(`update_status`);
                  }}
                >
                  <PlanStatusIcon
                    status={activePlan?.status}
                    style={{ color: getPlanStatusColor(activePlan.status, `dark`), marginBottom: `1px` }}
                  />
                </Grid>
              )}
            </Grid>
            <Grid
              container
              direction="row"
              wrap="nowrap"
              alignItems="center"
              justifyContent="center"
              style={{ width: `fit-content`, padding: `0px 13px `, borderLeft: `1px solid ${RoutalPalette.neutral10}` }}
            >
              <Grid
                container
                style={{
                  width: `20px`,
                  borderRadius: `0 2px 2px 0`,
                }}
                justifyContent="center"
                alignContent="center"
              >
                <PlanActionsMenu
                  plan={activePlan!}
                  hasDriverWithStops={drivers?.some((driver) => driver.stops.length > 0)}
                  onDeletePlan={() => {
                    navigate(routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id));
                  }}
                  onDispatchPlan={() => {
                    setDialog(`dispatch`);
                  }}
                  onUpdatePlanSettings={() => {
                    setDialog(`optimizerOptions`);
                  }}
                  onOptimizePlan={() => {
                    if (drivers?.every((driver) => driver.stops.length === 0)) {
                      if (activePlan) {
                        optimizePlan({
                          plan: activePlan,
                        });
                      }
                    } else {
                      setDialog(`optimize`);
                    }
                  }}
                  onOpenGeoFencesDialog={() => {
                    setDialog(`geofence_dialog`);
                  }}
                  popoverProps={{
                    hideArrow: true,
                    content: <More3Points />,
                    style: {
                      color: RoutalPalette.secondary60,
                      padding: 0,
                      margin: 0,
                      border: `unset`,
                      transform: `rotate(+90deg)`,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <AnalyticsDialog dialogOpen={Boolean(dialog === `analytics`)} onClose={() => setDialog(undefined)} />
      <OptimizationDialog
        dialogOpen={Boolean(dialog === `optimize`)}
        onClose={() => setDialog(undefined)}
        activePlan={activePlan}
        activeProject={activeProject}
        drivers={drivers}
      />
      <OptimizerOptionsDialog isOpen={Boolean(dialog === `optimizerOptions`)} onClose={() => setDialog(undefined)} />
      {activePlan && (
        <DispatchDialog
          plan={activePlan}
          handleCloseDialog={setDialog}
          dispatchDialogOpen={Boolean(dialog === `dispatch`)}
        />
      )}
      <GeoFencesDialogOverview
        dialogOpen={Boolean(dialog === `geofence_dialog`)}
        onCloseDialog={() => {
          setDialog(undefined);
        }}
      />
      {activePlan && dialog === `update_status` && (
        <PlanStatusDialog
          plans={[activePlan]}
          dialogOpen
          onCloseDialog={() => {
            setDialog(undefined);
          }}
          onUpdatePlanStatus={() => {
            setDialog(undefined);
          }}
        />
      )}
    </>
  );
};
